// The one drawer a patient joins the waitlist from, and changes a request in.
//
// Built to the design standard (blhClinicApp docs/design-standard.md) with the
// tokens in src/styles/blh-tokens.css: a drawer, never a modal, for a form; one
// column; visible labels; every choice a radio or a toggle of at least 44 px;
// no native select, time or date input. Problems are checked on submit, then
// live, each said next to its field and in a summary that takes focus and
// links to it. The server's refusals are said in its own words in that
// summary, and the form keeps everything the patient chose.
//
// What it may offer comes from the server (`askAt`: the sites that take
// requests online, the services a patient may wait for at each, and the
// clinicians who deliver them there, all named), so nothing is offered that the
// clinic would refuse, at any of its sites. The length is the service's: never
// asked. The model is requestForm.js.

import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
} from "@chakra-ui/react";
import {forwardRef, useEffect, useMemo, useRef, useState} from "react";
import {FiAlertTriangle, FiCheck} from "react-icons/fi";
import {describeBands} from "./describeBands";
import {optionsIncludingCurrent} from "./selectOptions";
import {
  DAYS,
  NOTICES,
  PARTS,
  bodyOf,
  dayInWords,
  defaultStayDays,
  draftOf,
  hasPart,
  problemsOf,
  staysWithin,
  togglePart,
} from "./requestForm";
import {formatMinutes} from "./serviceDurations";

const FIELD_IDS = {
  location: "wl-location",
  service: "wl-service",
  when: "wl-when",
};
const MANY_SERVICES = 8;
const TOUCH = "min-h-[var(--blh-control-height-touch)]";
const FOCUS =
  "focus-visible:outline focus-visible:outline-[length:var(--blh-focus-ring-width)] focus-visible:outline-offset-[var(--blh-focus-ring-offset)] focus-visible:outline-blh-focus";
const DATE = new Intl.DateTimeFormat("en-CA", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

/** A question answered by one choice: a fieldset of radios, its problem said under the legend. */
function ChoiceGroup({
  id,
  legend,
  hint,
  name,
  options,
  value,
  onChange,
  problem,
  children,
}) {
  const described =
    [hint && `${id}-hint`, problem && `${id}-problem`]
      .filter(Boolean)
      .join(" ") || undefined;
  return (
    <fieldset
      id={id}
      aria-describedby={described}
      className="m-0 min-w-0 border-0 p-0"
    >
      <legend className="mb-blh-100 p-0 text-blh-title font-semibold text-blh-ink">
        {legend}
      </legend>
      {hint && (
        <p
          id={`${id}-hint`}
          className="mb-blh-200 mt-0 text-blh-compact text-blh-ink-2"
        >
          {hint}
        </p>
      )}
      {problem && <Problem id={`${id}-problem`}>{problem}</Problem>}
      {children}
      <div className="flex flex-col gap-blh-200">
        {options.map((option) => {
          const chosen = option.value === value;
          return (
            <label
              key={option.value}
              className={`${TOUCH} flex cursor-pointer items-center gap-blh-300 rounded-blh-md border px-blh-300 py-blh-200 transition-colors duration-[var(--blh-motion-fast)] ${
                chosen
                  ? "border-blh-brand bg-blh-brand-soft"
                  : "border-blh-boundary bg-blh-raised"
              }`}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={chosen}
                onChange={() => onChange(option.value)}
                className={`h-5 w-5 shrink-0 accent-blh-brand ${FOCUS}`}
              />
              <span className="min-w-0">
                <span
                  className={`block text-blh-body text-blh-ink ${chosen ? "font-semibold" : ""}`}
                >
                  {option.label}
                </span>
                {option.hint && (
                  <span className="block text-blh-compact text-blh-ink-2">
                    {option.hint}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function Problem({id, children}) {
  return (
    <p
      id={id}
      className="mb-blh-200 mt-0 flex items-start gap-blh-100 text-blh-compact font-medium text-blh-danger"
    >
      <FiAlertTriangle aria-hidden="true" className="mt-[3px] shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/** Mornings, afternoons and evenings of the week, each a toggle: on is a check, a border and bold words. */
function PartsGrid({cells, onToggle}) {
  return (
    <div
      role="group"
      aria-label="The mornings, afternoons and evenings that work for you"
      className="-mt-blh-300 flex flex-col gap-blh-100"
    >
      <p className="m-0 text-blh-compact text-blh-ink-2">
        {PARTS.map((part) => `${part.label}: ${part.hint.toLowerCase()}`).join(
          ". "
        )}
        .
      </p>
      {DAYS.map((day) => (
        <div
          key={day.dayOfWeek}
          className="grid grid-cols-[3rem_repeat(3,minmax(0,1fr))] items-center gap-blh-100"
        >
          <span
            aria-hidden="true"
            className="text-blh-compact font-medium text-blh-ink-2"
          >
            {day.short}
          </span>
          {PARTS.map((part) => {
            const on = hasPart(cells, day.dayOfWeek, part.key);
            return (
              <button
                key={part.key}
                type="button"
                aria-pressed={on}
                aria-label={`${day.label} ${part.label.toLowerCase()}`}
                onClick={() => onToggle(day.dayOfWeek, part.key)}
                className={`${TOUCH} ${FOCUS} flex items-center justify-center gap-blh-100 rounded-blh-md border px-blh-100 text-blh-compact ${
                  on
                    ? "border-blh-brand bg-blh-brand-soft font-semibold text-blh-brand-ink"
                    : "border-blh-boundary bg-blh-raised text-blh-ink"
                }`}
              >
                {on && <FiCheck aria-hidden="true" className="shrink-0" />}
                <span className="truncate">{part.label}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** What stops the form, in one place: it takes focus, and each line leads to its field. */
const ProblemSummary = forwardRef(function ProblemSummary(
  {problems, refusal, onGo},
  ref
) {
  return (
    <div
      ref={ref}
      tabIndex={-1}
      aria-labelledby="wl-problems-title"
      className={`${FOCUS} mb-blh-400 rounded-blh-md border-2 border-blh-danger bg-blh-t-red p-blh-400`}
    >
      <h3
        id="wl-problems-title"
        className="m-0 mb-blh-200 text-blh-title font-semibold text-blh-ink"
      >
        There is a problem
      </h3>
      <ul className="m-0 flex list-none flex-col gap-blh-100 p-0">
        {problems.map((problem) => (
          <li key={problem.field}>
            <button
              type="button"
              onClick={() => onGo(problem.field)}
              className={`${FOCUS} text-left text-blh-body font-medium text-blh-danger underline`}
            >
              {problem.message}
            </button>
          </li>
        ))}
        {refusal && <li className="text-blh-body text-blh-ink">{refusal}</li>}
      </ul>
    </div>
  );
});

/** The server's words for a refusal, or what to do when there was no answer. */
function wordsOf(error) {
  const said = error?.response?.data?.error;
  if (typeof said === "string" && said.trim()) return said;
  return "Your request was not sent. Check your connection, then try again.";
}

export default function WaitlistRequestDrawer({
  isOpen,
  onClose,
  entry,
  askAt = [],
  locationName = "",
  serviceName = "",
  clinicianName = "",
  onSubmit,
}) {
  const editing = Boolean(entry);
  const siteIds = askAt.map((site) => site.locationId);
  const [draft, setDraft] = useState(() => draftOf(entry, {siteIds}));
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState("");
  const [serviceQuery, setServiceQuery] = useState("");
  const titleRef = useRef(null);
  const summaryRef = useRef(null);
  const [summaryWanted, setSummaryWanted] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setDraft(draftOf(entry, {siteIds}));
    setTried(false);
    setRefusal("");
    setServiceQuery("");
    // Opened again for another request (or a new one): what it holds is that one, as it is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entry]);

  useEffect(() => {
    if (summaryWanted) summaryRef.current?.focus();
  }, [summaryWanted]);

  const update = (patch) => setDraft((current) => ({...current, ...patch}));
  // The site, its services and their clinicians, as the server names them.
  const site = askAt.find((one) => one.locationId === draft.locationId) || null;
  // The site's today and the latest last day the patient's own request may have, on its calendar (the server's
  // lastDay.js): the stays offered end by then. A stay past it gives way to the longest that fits.
  const lastDay = site?.lastDay ?? null;
  const offeredStays = useMemo(() => staysWithin(lastDay), [lastDay]);
  useEffect(() => {
    if (draft.stay.until !== undefined) return;
    if (offeredStays.some((stay) => stay.days === draft.stay.days)) return;
    setDraft((current) => ({...current, stay: {days: defaultStayDays(offeredStays)}}));
  }, [offeredStays, draft.stay]);
  const service =
    site?.services.find((one) => String(one.serviceId) === draft.serviceId) ||
    null;
  const sameService = editing && draft.serviceId === String(entry.serviceId);

  const serviceOptions = useMemo(() => {
    const known = (site?.services || []).map((one) => ({
      value: String(one.serviceId),
      label: one.name,
    }));
    if (!editing) return known;
    // The service a request holds stays shown, said as no longer offered only where the site's list is known.
    const current = serviceName || "This service";
    return optionsIncludingCurrent(
      known,
      String(entry.serviceId),
      site ? `${current} (no longer offered)` : current
    );
  }, [site, editing, entry, serviceName]);
  const shownServices = serviceQuery.trim()
    ? serviceOptions.filter(
        (option) =>
          option.value === draft.serviceId ||
          option.label.toLowerCase().includes(serviceQuery.trim().toLowerCase())
      )
    : serviceOptions;

  const clinicianOptions = useMemo(() => {
    const known = (service?.clinicians || []).map((one) => ({
      value: one.clinicianId,
      label: one.name,
    }));
    const withCurrent = sameService
      ? optionsIncludingCurrent(
          known,
          entry.clinicianId || "",
          `${clinicianName || "Your clinician"} (not available here)`
        )
      : known;
    return [
      {
        value: "",
        label: "Anyone available",
        hint: "The first time that fits, with any clinician who gives this service",
      },
      ...withCurrent,
    ];
  }, [service, sameService, entry, clinicianName]);

  const chooseSite = (locationId) =>
    update({locationId, serviceId: "", clinicianId: ""});
  const chooseService = (serviceId) => {
    const back = editing && serviceId === String(entry.serviceId);
    update({serviceId, clinicianId: back ? entry.clinicianId || "" : ""});
  };

  const problems = tried ? problemsOf(draft) : [];
  const problemOf = (field) =>
    problems.find((problem) => problem.field === field)?.message;
  const goTo = (field) =>
    document
      .getElementById(FIELD_IDS[field])
      ?.querySelector("input, button")
      ?.focus();

  async function submit(event) {
    event.preventDefault();
    if (saving) return;
    setTried(true);
    setRefusal("");
    if (problemsOf(draft).length > 0) {
      setSummaryWanted((n) => n + 1);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(bodyOf(draft, {now: Date.now(), lastDay}));
    } catch (error) {
      setRefusal(wordsOf(error));
      setSummaryWanted((n) => n + 1);
    } finally {
      setSaving(false);
    }
  }

  const notices = NOTICES.some(
    (notice) => notice.minutes === draft.noticeMinutes
  )
    ? NOTICES
    : [
        ...NOTICES,
        {
          minutes: draft.noticeMinutes,
          label: `At least ${formatMinutes(draft.noticeMinutes)}`,
        },
      ];
  const stays = [
    ...(editing && Number.isFinite(entry.validUntil)
      ? [
          {
            value: "kept",
            label: `Until ${DATE.format(new Date(entry.validUntil))}`,
            hint: "As it is now",
          },
        ]
      : []),
    ...offeredStays.map((stay) => ({value: String(stay.days), label: stay.label})),
  ];
  const whenOptions = [
    {
      value: "any",
      label: "Any day, any time",
      hint: "The widest choice: every opening can reach you",
    },
    // Times the desk set that are not whole parts of a day: kept unless the patient chooses new ones.
    ...(draft.keptBands.length > 0
      ? [
          {
            value: "kept",
            label: "Keep my current times",
            hint: describeBands(draft.keptBands),
          },
        ]
      : []),
    {value: "parts", label: "Only some mornings, afternoons or evenings"},
  ];
  const title = editing ? "Change your request" : "Join the waitlist";
  const action = editing ? "Save changes" : "Join the waitlist";

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement="right"
      size={{base: "full", md: "md"}}
      initialFocusRef={titleRef}
    >
      <DrawerOverlay />
      <DrawerContent
        bg="var(--blh-raised)"
        color="var(--blh-ink)"
        fontFamily="var(--blh-font-family-sans)"
      >
        <DrawerCloseButton
          aria-label="Close"
          boxSize="var(--blh-control-height-touch)"
          top="var(--blh-space-200)"
        />
        <DrawerHeader
          as="h2"
          ref={titleRef}
          tabIndex={-1}
          className="text-blh-display font-semibold text-blh-ink"
          outline="none"
        >
          {title}
        </DrawerHeader>
        <DrawerBody>
          <form
            id="wl-request-form"
            noValidate
            onSubmit={submit}
            className="flex flex-col gap-blh-600 pb-blh-400"
          >
            <p className="m-0 text-blh-body text-blh-ink-2">
              {editing
                ? "Changing your times or notice keeps your place in the queue."
                : "Tell us what you need and when you can come in. We will let you know when a time opens up."}
            </p>
            {(problems.length > 0 || refusal) && (
              <ProblemSummary
                ref={summaryRef}
                problems={problems}
                refusal={refusal}
                onGo={goTo}
              />
            )}

            {!editing && askAt.length > 1 ? (
              <ChoiceGroup
                id={FIELD_IDS.location}
                legend="Location"
                name="wl-location"
                options={askAt.map((one) => ({
                  value: one.locationId,
                  label: one.name,
                }))}
                value={draft.locationId}
                onChange={chooseSite}
                problem={problemOf("location")}
              />
            ) : (
              <p className="m-0 text-blh-body text-blh-ink">
                <span className="font-semibold">Location:</span>{" "}
                {site?.name || locationName}
              </p>
            )}

            {draft.locationId && (
              <ChoiceGroup
                id={FIELD_IDS.service}
                legend="What you are waiting for"
                name="wl-service"
                options={shownServices}
                value={draft.serviceId}
                onChange={chooseService}
                problem={problemOf("service")}
              >
                {serviceOptions.length > MANY_SERVICES && (
                  <div className="mb-blh-200 flex flex-col gap-blh-100">
                    <label
                      htmlFor="wl-service-find"
                      className="text-blh-compact font-medium text-blh-ink-2"
                    >
                      Find a service
                    </label>
                    <input
                      id="wl-service-find"
                      type="search"
                      value={serviceQuery}
                      onChange={(event) => setServiceQuery(event.target.value)}
                      className={`${TOUCH} ${FOCUS} rounded-blh-md border border-blh-boundary bg-blh-raised px-blh-300 text-blh-body text-blh-ink`}
                    />
                  </div>
                )}
              </ChoiceGroup>
            )}

            {editing && draft.serviceId && !sameService && (
              <p
                role="note"
                className="m-0 flex items-start gap-blh-200 rounded-blh-md bg-blh-t-amber p-blh-300 text-blh-body text-blh-ink"
              >
                <FiAlertTriangle
                  aria-hidden="true"
                  className="mt-[3px] shrink-0 text-blh-warning"
                />
                <span>
                  Changing what you are waiting for starts a new request, at the
                  back of the queue.
                </span>
              </p>
            )}

            {draft.serviceId && (
              <ChoiceGroup
                id="wl-clinician"
                legend="Who you would like to see"
                name="wl-clinician"
                options={clinicianOptions}
                value={draft.clinicianId}
                onChange={(clinicianId) => update({clinicianId})}
              />
            )}

            <ChoiceGroup
              id={FIELD_IDS.when}
              legend="When you can come in"
              name="wl-when"
              options={whenOptions}
              value={draft.when}
              onChange={(when) => update({when})}
              problem={problemOf("when")}
            />
            {draft.when === "parts" && (
              <PartsGrid
                cells={draft.cells}
                onToggle={(day, part) =>
                  update({cells: togglePart(draft.cells, day, part)})
                }
              />
            )}

            <ChoiceGroup
              id="wl-notice"
              legend="How much notice you need"
              name="wl-notice"
              options={notices.map((notice) => ({
                value: String(notice.minutes),
                label: notice.label,
              }))}
              value={String(draft.noticeMinutes)}
              onChange={(value) => update({noticeMinutes: Number(value)})}
            />

            <ChoiceGroup
              id="wl-stay"
              legend="How long to stay on the list"
              hint={
                lastDay?.latest
                  ? `This location keeps a request until ${dayInWords(lastDay.latest)} at the latest.`
                  : undefined
              }
              name="wl-stay"
              options={stays}
              value={
                draft.stay.until !== undefined
                  ? "kept"
                  : String(draft.stay.days)
              }
              onChange={(value) =>
                update({
                  stay:
                    value === "kept"
                      ? {until: entry.validUntil}
                      : {days: Number(value)},
                })
              }
            />
          </form>
        </DrawerBody>
        <DrawerFooter
          gap="var(--blh-space-200)"
          borderTop="1px solid var(--blh-line)"
        >
          <button
            type="button"
            onClick={onClose}
            className={`${TOUCH} ${FOCUS} rounded-blh-md border border-blh-boundary bg-blh-raised px-blh-400 text-blh-body font-medium text-blh-ink`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="wl-request-form"
            aria-disabled={saving || undefined}
            className={`${TOUCH} ${FOCUS} rounded-blh-md bg-blh-brand px-blh-400 text-blh-body font-semibold text-blh-on-brand hover:bg-blh-brand-ink ${saving ? "opacity-80" : ""}`}
          >
            {saving ? (editing ? "Saving…" : "Joining…") : action}
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
